// src/MarketTrendWidget.js

import widgetTemplate from './mt-widget.html';
import errorTemplate from '../shared/error.html';
import trendsTemplate from '../shared/trends.html';
import * as trends from '../shared/trends';
import * as helpers from '../shared/helpers';
import { bootstrapStyle } from '../shared/styles';
import { library, dom } from '@fortawesome/fontawesome-svg-core'
import { all } from '@awesome.me/kit-bd5fe14630/icons'

class MarketTrendWidget extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        const style = document.createElement('style');
        style.textContent = bootstrapStyle;
        this.shadowRoot.appendChild(style);

        const template = document.createElement('section');
        template.classList.add('ha-widget');
        template.innerHTML = widgetTemplate;
        this.shadowRoot.appendChild(template);

        library.add(...all);
        dom.watch({
            autoReplaceSvgRoot: this.shadowRoot,
            observeMutationsRoot: this.shadowRoot
        });
    }

    connectedCallback() {
        this.baseUrl = helpers.getBaseUrl();
        this.baseApiUrl = helpers.getBaseApiUrl();
        this.primaryColor = this.getAttribute('primary-color') || '#024267';
        this.secondaryColor = this.getAttribute('secondary-color') || '#F79322';
        this.widgetBackground = this.getAttribute('background-color') || 'transparent';
        this.fontFamily = this.getAttribute('font-family') || '"Helvetica Neue", Helvetica, Arial, sans-serif'
        this.fontUrl = this.getAttribute('font-url') || '';
        this.showOverlay = this.getAttribute('show-overlay') || 'no';
        this.primaryErrorText = this.getAttribute('primary-error-text') || 'No market trend could be generated for:';
        this.secondaryErrorText = this.getAttribute('secondary-error-text') || 'Want a professional report from an agent in the area?';
        this.errorPhone = this.getAttribute('error-phone') || null;
        this.errorEmail = this.getAttribute('error-email') || 'service@homeadvantage.com';
        this.init();
    }

    init() {
        helpers.setColors(this);
        this.setupSearchInput();
    }

    setupSearchInput() {
        const searchInput = this.shadowRoot.getElementById('search-input');
        const searchGroup = this.shadowRoot.getElementById('wrapper');
        const showOverlay = this.showOverlay.toLowerCase() === 'yes';

        if (showOverlay) {
            const overlay = this.shadowRoot.querySelector('.ha-overlay');
            overlay.style.width = '100%';
            overlay.style.padding = '4em';
            overlay.style.backgroundColor = this.getAttribute('overlay-background') || this.primaryColor;

            const overlayHeading = this.getAttribute('overlay-heading');
            if (overlayHeading) {
                const heading = document.createElement('h1');
                heading.classList.add('text-white');
                heading.innerHTML = overlayHeading;
                searchGroup.parentElement.insertBefore(heading, searchGroup);
            }

            const overlaySubHeading = this.getAttribute('overlay-sub-heading');
            if (overlaySubHeading) {
                const subHeading = document.createElement('h6');
                subHeading.classList.add('text-white');;
                subHeading.innerHTML = overlaySubHeading;
                searchGroup.parentElement.insertBefore(subHeading, searchGroup);
            }
        }

        const placeholder = this.getAttribute('text-placeholder') || 'Search by City, Zip, Neighborhood...';
        searchInput.setAttribute('placeholder', placeholder);

        let timeoutId;
        let abortController = new AbortController();

        searchInput.addEventListener('input', (e) => {
            const inputValue = e.target.value.toLowerCase();
            helpers.closeAllLists(this);

            if (inputValue.length < 5) {
                return false;
            }
            const spinner = this.shadowRoot.querySelector('.searching');
            spinner.classList.remove('d-none');

            if (abortController) {
                abortController.abort();
            }
            abortController = new AbortController();

            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                this.getArea(inputValue, abortController.signal, searchInput);
            }, 300);
        });
    }

    async getArea(text, signal, searchInput) {
        const endpoint = 'api/services/app/HomeSearch/GetSearchArea';
        const queryParams = { input: text };
        const url = `${this.baseApiUrl}${endpoint}?${helpers.serializeParams(queryParams)}`;

        try {
            const response = await fetch(url, { signal, headers: helpers.getHeaders() });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            const autocompleteItems = this.shadowRoot.getElementById('autocomplete-items');
            autocompleteItems.style.display = 'block';

            if (data.result.areas.length > 0) {
                data.result.areas.forEach(suggestion => {
                    const listItem = document.createElement('li');
                    listItem.innerHTML = suggestion.label;
                    listItem.addEventListener('click', () => {
                        searchInput.value = suggestion.label;
                        helpers.closeAllLists(this);
                        this.getTrend(suggestion.label);
                    });
                    autocompleteItems.appendChild(listItem);
                });
            }
            else {
                const listItem = document.createElement('li');
                listItem.innerHTML = 'No results found';
                autocompleteItems.appendChild(listItem);
            }

            const spinner = this.shadowRoot.querySelector('.searching');
            spinner.classList.add('d-none');
        } catch (error) {
            console.error('Fetch error:', error);
        }
    }

    async getTrend(search) {
        var hasError = false;

        const errorContainer = this.shadowRoot.querySelector('.error-container');
        errorContainer.classList.add('d-none');
        errorContainer.innerHTML = errorTemplate;

        const loading = this.shadowRoot.querySelector('.loading-overlay').parentElement;
        loading.classList.remove('d-none');
        loading.scrollIntoView({ behavior: 'smooth' });

        const statsContainer = this.shadowRoot.getElementById("statsContainer");
        statsContainer.classList.add('d-none');
        statsContainer.innerHTML = trendsTemplate;

        try {
            const trendsDataEndpoint = 'api/services/app/HomeSearch/GetTrendsData';
            const trendsQueryParams = { search: search };
            const trendsUrl = `${this.baseApiUrl}${trendsDataEndpoint}?${helpers.serializeParams(trendsQueryParams)}`;

            const trendsResponse = await fetch(trendsUrl, { headers: helpers.getHeaders() });
            if (!trendsResponse.ok) {
                throw new Error('Network response was not ok');
            }

            const trendsData = await trendsResponse.json();
            trends.createMarketTrends(this, trendsData.result.areaTrendStats.trends);
            trends.createPricesAndSalesGraph(this, trendsData.result.areaTrendPricesAndSales.trends);
            trends.createDaysOnMarketGraph(this, trendsData.result.areaTrendsDaysOnMarket.trends);
            trends.createDaysOfInventoryGraph(this, trendsData.result.areaTrendsDaysOfInventory.trends, trendsData.result.areaTrendPricesAndSales.trends);
        } catch (error) {
            hasError = true;
            console.error('Fetch error:', error);
        }

        if (hasError) {
            errorContainer.querySelector('.error-text').innerHTML = this.primaryErrorText + '<br/>' + search;
            errorContainer.querySelector('.secondary-text').innerHTML = this.secondaryErrorText;

            const contactTemplate = errorContainer.querySelector('.error-contact');
            if (this.errorPhone) {
                const phoneTemplate = document.createElement('div');
                phoneTemplate.classList.add('col', 'text-center');
                phoneTemplate.innerHTML = `<a href="tel:${this.errorPhone}" class="text-primary"><h3>Give us a call</h3><i class="fa-light fa-phone display-3"></i></a>`

                contactTemplate.appendChild(phoneTemplate);
            }
            const emailTemplate = document.createElement('div');
            emailTemplate.classList.add('col', 'text-center');
            emailTemplate.innerHTML = `<a href="mailto:${this.errorEmail}?subject=Help with Market Trend" class="text-primary"><h3>Send us an e-mail</h3><i class="fa-light fa-envelope display-3"></i></a>`

            contactTemplate.appendChild(emailTemplate);
            errorContainer.classList.remove('d-none');
            errorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        else {
            statsContainer.classList.remove('d-none');
            statsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        loading.classList.add('d-none');
    }
}

if (!customElements.get('market-trend-widget')) {
    customElements.define('market-trend-widget', MarketTrendWidget);
}

export default MarketTrendWidget;
